import type { EntityCrudPageProps } from '../../components/EntityCrudPage';

const STATE_FIELDS = [
  { key: 'state', label: 'State', type: 'text' as const, required: true, width: 0.5 as const, showInTable: true },
  { key: 'abbreviation', label: 'Abbr.', type: 'text' as const, required: true, width: 0.5 as const, showInTable: true },
];

export const entityCompliance: EntityCrudPageProps = {
  title: 'Entity Compliance',
  resourcePath: '/admin/entity-compliance',
  getRowLabel: (r) => String(r.entityKey ?? r._id),
  fields: [
    { key: 'entityKey', label: 'Entity Key', type: 'text', required: true, width: 0.5, showInTable: true, helperText: 'e.g. 501c3' },
    { key: 'name', label: 'Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'form990Required', label: 'Form 990 Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'unrelatedBusinessLimit', label: 'Unrelated Business Limit', type: 'bool', width: 0.5, showInTable: true },
    { key: 'politicalActivityProhibited', label: 'Political Activity Prohibited', type: 'bool', width: 0.5 },
    { key: 'privateBenefitProhibited', label: 'Private Benefit Prohibited', type: 'bool', width: 0.5 },
    { key: 'donorDisclosure', label: 'Donor Disclosure', type: 'bool', width: 0.5, showInTable: true },
    { key: 'publicCharityTest', label: 'Public Charity Test', type: 'bool', width: 0.5 },
    { key: 'specialNotes', label: 'Special Notes', type: 'multiline' },
  ],
};

export const stateCharitableSolicitation: EntityCrudPageProps = {
  title: 'State Charitable Solicitation',
  resourcePath: '/admin/state-charitable-solicitation',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'registrationRequired', label: 'Registration Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'agencyDivision', label: 'Agency Division', type: 'text' },
    { key: 'formName', label: 'Form Name', type: 'text', width: 0.5 },
    {
      key: 'renewalPeriod',
      label: 'Renewal Period',
      type: 'select',
      width: 0.5,
      showInTable: true,
      options: [
        { value: 'Annual', label: 'Annual' },
        { value: 'Biennial', label: 'Biennial' },
        { value: 'N/A', label: 'N/A' },
      ],
    },
    { key: 'initialFee', label: 'Initial Fee', type: 'text', width: 0.5 },
    { key: 'renewalFee', label: 'Renewal Fee', type: 'text', width: 0.5 },
    { key: 'revenueThresholdForExemption', label: 'Revenue Threshold For Exemption', type: 'text' },
    { key: 'religiousExemption', label: 'Religious Exemption', type: 'bool', width: 0.5 },
    { key: 'smallOrgExemption', label: 'Small Org Exemption', type: 'bool', width: 0.5 },
    { key: 'smallOrgThreshold', label: 'Small Org Threshold', type: 'text', width: 0.5 },
    { key: 'educationalExemption', label: 'Educational Exemption', type: 'bool', width: 0.5 },
    { key: 'acceptsURS', label: 'Accepts URS', type: 'bool', width: 0.5 },
    { key: 'onlineFilingAvailable', label: 'Online Filing Available', type: 'bool', width: 0.5 },
    { key: 'deadlineBasis', label: 'Deadline Basis (legacy text)', type: 'text', helperText: 'Free-form summary. Prefer using Deadline Rules below for new entries.' },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      showInTable: true,
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition is the deadline.',
    },
    { key: 'foreignQualificationRequired', label: 'Foreign Qualification Required', type: 'bool', width: 0.5 },
    { key: 'disclosureRequired', label: 'Disclosure Required', type: 'bool', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateAnnualReports: EntityCrudPageProps = {
  title: 'State Annual Reports',
  resourcePath: '/admin/state-annual-reports',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'required', label: 'Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    {
      key: 'frequency',
      label: 'Frequency',
      type: 'select',
      width: 0.5,
      showInTable: true,
      options: [
        { value: 'Annual', label: 'Annual' },
        { value: 'Biennial', label: 'Biennial' },
        { value: 'Decennial', label: 'Decennial' },
        { value: 'Quinquennial', label: 'Quinquennial' },
        { value: 'N/A', label: 'N/A' },
      ],
    },
    { key: 'fee', label: 'Fee', type: 'text', width: 0.5, showInTable: true },
    { key: 'deadline', label: 'Deadline (legacy text)', type: 'text', width: 0.5, helperText: 'Free-form summary. Prefer using Deadline Rules below for new entries.' },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      showInTable: true,
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition is the deadline.',
    },
    { key: 'onlineFilingAvailable', label: 'Online Filing Available', type: 'bool', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateTaxExemptions: EntityCrudPageProps = {
  title: 'State Tax Exemptions',
  resourcePath: '/admin/state-tax-exemptions',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'hasStateIncomeTax', label: 'Has State Income Tax', type: 'bool', width: 0.5, showInTable: true },
    {
      key: 'separateExemptionApplicationRequired',
      label: 'Separate Exemption App Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'incomeTaxExemptionAgency', label: 'Income Tax Exemption Agency', type: 'text' },
    { key: 'salesTaxExemptionAvailable', label: 'Sales Tax Exemption Available', type: 'bool', width: 0.5 },
    { key: 'salesTaxExemptionProcess', label: 'Sales Tax Exemption Process', type: 'multiline' },
    { key: 'salesTaxRenewalRequired', label: 'Sales Tax Renewal Required', type: 'bool', width: 0.5 },
    { key: 'salesTaxRenewalFrequency', label: 'Sales Tax Renewal Frequency', type: 'text', width: 0.5 },
    {
      key: 'propertyTaxExemptionAvailable',
      label: 'Property Tax Exemption Available',
      type: 'bool',
      width: 0.5,
    },
    { key: 'propertyTaxExemptionProcess', label: 'Property Tax Exemption Process', type: 'multiline' },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateFundraisingRequirements: EntityCrudPageProps = {
  title: 'State Fundraising Requirements',
  resourcePath: '/admin/state-fundraising-requirements',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    {
      key: 'professionalFundraiserRegistrationRequired',
      label: 'Pro Fundraiser Reg. Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    {
      key: 'professionalSolicitorRegistrationRequired',
      label: 'Pro Solicitor Reg. Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'suretyBondRequired', label: 'Surety Bond Required', type: 'bool', width: 0.5 },
    { key: 'suretyBondAmount', label: 'Surety Bond Amount', type: 'text', width: 0.5 },
    { key: 'rafflesAllowed', label: 'Raffles Allowed', type: 'bool', width: 0.5 },
    { key: 'raffleLicenseRequired', label: 'Raffle License Required', type: 'bool', width: 0.5 },
    { key: 'raffleNotes', label: 'Raffle Notes', type: 'multiline' },
    {
      key: 'charitableGamingLicenseRequired',
      label: 'Charitable Gaming License Required',
      type: 'bool',
      width: 0.5,
    },
    { key: 'charitableGamingNotes', label: 'Charitable Gaming Notes', type: 'multiline' },
    {
      key: 'onlineSolicitationTriggers',
      label: 'Online Solicitation Triggers',
      type: 'bool',
      width: 0.5,
    },
    {
      key: 'charlestonPrinciplesAdopted',
      label: 'Charleston Principles Adopted',
      type: 'bool',
      width: 0.5,
    },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateEmploymentRequirements: EntityCrudPageProps = {
  title: 'State Employment Requirements',
  resourcePath: '/admin/state-employment-requirements',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'hasStateIncomeTax', label: 'Has State Income Tax', type: 'bool', width: 0.5, showInTable: true },
    {
      key: 'stateWithholdingRequired',
      label: 'State Withholding Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'withholdingAgency', label: 'Withholding Agency', type: 'text' },
    { key: 'stateUnemploymentRequired', label: 'State Unemployment Required', type: 'bool', width: 0.5 },
    { key: 'unemploymentAgency', label: 'Unemployment Agency', type: 'text' },
    {
      key: 'nonprofitCanSelfInsureUnemployment',
      label: 'Nonprofit Can Self-Insure Unemployment',
      type: 'bool',
      width: 0.5,
    },
    {
      key: 'workersCompensationRequired',
      label: "Workers' Comp Required",
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'workersCompNotes', label: "Workers' Comp Notes", type: 'multiline' },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const federalFilings: EntityCrudPageProps = {
  title: 'Federal Filings',
  resourcePath: '/admin/federal-filings',
  getRowLabel: (r) => String(r.formNumber ?? r._id),
  fields: [
    { key: 'formNumber', label: 'Form #', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'name', label: 'Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'description', label: 'Description', type: 'multiline' },
    { key: 'whoMustFile', label: 'Who Must File', type: 'multiline', showInTable: true },
    { key: 'threshold', label: 'Threshold', type: 'text', width: 0.5 },
    { key: 'deadline', label: 'Deadline (legacy text)', type: 'text', width: 0.5, helperText: 'Free-form. Prefer Deadline Rules below.' },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      showInTable: true,
      helperText: 'Add one or more conditions joined by OR; the earliest applicable wins. Add per-rule conditions for thresholds (e.g. revenue ≥ $200k).',
    },
    { key: 'extensionForm', label: 'Extension Form', type: 'text', width: 0.5 },
    { key: 'extensionDuration', label: 'Extension Duration', type: 'text', width: 0.5 },
    { key: 'penalty', label: 'Penalty', type: 'multiline' },
    {
      key: 'electronicFilingRequired',
      label: 'Electronic Filing Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'frequency', label: 'Frequency', type: 'text', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const form990Schedules: EntityCrudPageProps = {
  title: 'Form 990 Schedules',
  resourcePath: '/admin/form-990-schedules',
  getRowLabel: (r) => `Schedule ${r.schedule ?? r._id}`,
  fields: [
    { key: 'schedule', label: 'Schedule', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'name', label: 'Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'description', label: 'Description', type: 'multiline' },
    { key: 'requiredWhen', label: 'Required When', type: 'multiline', showInTable: true },
    { key: 'partIVLine', label: 'Part IV Line', type: 'text', width: 0.5 },
    {
      key: 'applicableForms',
      label: 'Applicable Forms',
      type: 'string-array',
      width: 0.5,
      showInTable: true,
      helperText: 'Comma-separated, e.g. 990, 990-EZ',
    },
  ],
};

export const questions: EntityCrudPageProps = {
  title: 'Questions',
  resourcePath: '/admin/questions',
  getRowLabel: (r) => String(r.questionId ?? r._id),
  fields: [
    { key: 'questionId', label: 'Question ID', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'type', label: 'Type', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'title', label: 'Title', type: 'text', showInTable: true },
    { key: 'subtitle', label: 'Subtitle', type: 'multiline' },
    { key: 'required', label: 'Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'order', label: 'Order', type: 'number', width: 0.5, showInTable: true },
    { key: 'options', label: 'Options', type: 'json', helperText: 'Array of { v, l, d } objects.' },
  ],
};

export const ENTITY_CONFIGS: Record<string, EntityCrudPageProps> = {
  'entity-compliance': entityCompliance,
  'state-charitable-solicitation': stateCharitableSolicitation,
  'state-annual-reports': stateAnnualReports,
  'state-tax-exemptions': stateTaxExemptions,
  'state-fundraising-requirements': stateFundraisingRequirements,
  'state-employment-requirements': stateEmploymentRequirements,
  'federal-filings': federalFilings,
  'form-990-schedules': form990Schedules,
  questions,
};
